import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Copy, ShieldCheck } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormError,
  Input,
  PageHeader,
} from "../../components/ui";
import { requireUser } from "../../features/auth/guards";
import { authClient } from "../../lib/auth-client";
import { useTRPC } from "../../lib/trpc";

export const Route = createFileRoute("/account/security")({
  beforeLoad: async ({ context, location }) => {
    await requireUser(context, location.href);
  },
  component: AccountSecurity,
});

const MIN_PASSWORD = 12;

function AccountSecurity() {
  const trpc = useTRPC();
  const me = useQuery(trpc.me.get.queryOptions());
  const firstWorkspace = me.data?.workspaces[0];
  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8 wrap-anywhere">
      <Link
        to={firstWorkspace ? "/w/$slug" : "/"}
        params={firstWorkspace ? { slug: firstWorkspace.slug } : {}}
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" /> Back
      </Link>
      <PageHeader
        title="Account security"
        description={me.data ? `Signed in as ${me.data.user.email}` : undefined}
      />
      <div className="flex flex-col gap-6">
        <TwoFactorCard enabled={Boolean(me.data?.user.twoFactorEnabled)} />
        <ChangePasswordCard />
      </div>
    </main>
  );
}

type Step =
  | { kind: "idle" }
  | { kind: "verify"; qr: string; secret: string; backupCodes: string[] };

function TwoFactorCard({ enabled }: { enabled: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step>({ kind: "idle" });
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.pathKey() });

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    const { data, error: enableError } = await authClient.twoFactor.enable({ password });
    setPending(false);
    if (enableError || !data) {
      setError(
        enableError?.status === 429
          ? "Too many attempts. Wait a minute."
          : "That password isn't right.",
      );
      return;
    }
    if (data.method !== "totp") {
      setError("Authenticator-app setup isn't available. Contact support.");
      return;
    }
    const secret = new URL(data.totpURI).searchParams.get("secret") ?? "";
    // Rendered locally as a data: URL; the secret never leaves the browser.
    const qr = await QRCode.toDataURL(data.totpURI, { margin: 1, width: 192 });
    setPassword("");
    setStep({ kind: "verify", qr, secret, backupCodes: data.backupCodes });
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    const { error: verifyError } = await authClient.twoFactor.verifyTotp({
      code: code.replace(/\s/g, ""),
    });
    setPending(false);
    if (verifyError) {
      setError("That code didn't work. Check your app's clock and try the newest code.");
      return;
    }
    toast.success("Two-factor authentication is on");
    setStep({ kind: "idle" });
    setCode("");
    await refresh();
  };

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    const { error: disableError } = await authClient.twoFactor.disable({ password });
    setPending(false);
    if (disableError) {
      setError(
        disableError.status === 429
          ? "Too many attempts. Wait a minute."
          : "That password isn't right.",
      );
      return;
    }
    setPassword("");
    toast.success("Two-factor authentication is off");
    await refresh();
  };

  return (
    <Card>
      <CardHeader
        title="Two-factor authentication"
        description="A code from an authenticator app (1Password, Google Authenticator, Authy…) in addition to your password."
        actions={
          enabled ? (
            <Badge tone="success">
              <ShieldCheck className="size-3" aria-hidden /> On
            </Badge>
          ) : (
            <Badge>Off</Badge>
          )
        }
      />
      <CardBody>
        {step.kind === "verify" ? (
          <form onSubmit={(e) => void verify(e)} className="flex flex-col gap-5" noValidate>
            <div className="flex flex-col gap-4 sm:flex-row">
              <img
                src={step.qr}
                alt="QR code for your authenticator app"
                className="size-48 rounded-md border border-border bg-white p-1"
              />
              <div className="flex flex-col gap-2 text-sm">
                <p>1. Scan this code with your authenticator app.</p>
                <p className="text-muted">
                  Can't scan? Enter this key:{" "}
                  <code className="font-mono break-all text-text">{step.secret}</code>
                </p>
                <p className="mt-2">
                  2. Save these backup codes somewhere safe. Each works once if you lose your phone.
                  They won't be shown again.
                </p>
                <BackupCodes codes={step.backupCodes} />
              </div>
            </div>
            <Field label="3. Enter the 6-digit code from the app">
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <FormError message={error} />
            <div className="flex gap-2">
              <Button type="submit" loading={pending} disabled={code.length < 6}>
                Turn on
              </Button>
              <Button variant="secondary" onClick={() => setStep({ kind: "idle" })}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={(e) => void (enabled ? disable(e) : start(e))}
            className="flex flex-col gap-4 sm:max-w-sm"
            noValidate
          >
            <Field label="Confirm your password">
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <FormError message={error} />
            <Button
              type="submit"
              variant={enabled ? "danger" : "primary"}
              loading={pending}
              disabled={!password}
            >
              {enabled ? "Turn off two-factor authentication" : "Set up two-factor authentication"}
            </Button>
          </form>
        )}
      </CardBody>
    </Card>
  );
}

function BackupCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const t = copied ? setTimeout(() => setCopied(false), 2000) : undefined;
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <div className="rounded-md border border-border bg-surface-muted p-3">
      <ul className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        onClick={() =>
          void navigator.clipboard.writeText(codes.join("\n")).then(() => setCopied(true))
        }
      >
        <Copy className="size-4" /> {copied ? "Copied" : "Copy codes"}
      </Button>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (next.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters.`);
    if (next !== confirm) return setError("The new passwords don't match.");
    setError(undefined);
    setPending(true);
    const { error: changeError } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      revokeOtherSessions: true,
    });
    setPending(false);
    if (changeError) {
      setError(
        changeError.status === 429
          ? "Too many attempts. Try again in a few minutes."
          : (changeError.message ?? "Couldn't change your password."),
      );
      return;
    }
    setCurrent("");
    setNext("");
    setConfirm("");
    toast.success("Password changed. Other devices were signed out.");
  };

  return (
    <Card>
      <CardHeader title="Password" description="Changing it signs you out on every other device." />
      <CardBody>
        <form
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-4 sm:max-w-sm"
          noValidate
        >
          <Field label="Current password">
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field
            label="New password"
            hint={`At least ${MIN_PASSWORD} characters. Passwords found in known breaches are refused.`}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          <FormError message={error} />
          <Button type="submit" loading={pending} disabled={!current || !next || !confirm}>
            Change password
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
